/**
 * USA-Specific Constants
 * Constants for US states, time zones, insurance providers, etc.
 */

/**
 * US States with full names and abbreviations
 */
export const US_STATES = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
  { label: 'District of Columbia', value: 'DC' },
] as const;

/**
 * US Time Zones
 */
export const US_TIME_ZONES = [
  { label: 'Eastern Time (ET)', value: 'America/New_York' },
  { label: 'Central Time (CT)', value: 'America/Chicago' },
  { label: 'Mountain Time (MT)', value: 'America/Denver' },
  { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
  { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
  { label: 'Hawaii-Aleutian Time (HST)', value: 'Pacific/Honolulu' },
] as const;

/**
 * Major US Insurance Providers
 */
export const US_INSURANCE_PROVIDERS = [
  // Government Programs
  { label: 'Medicare', value: 'medicare', category: 'government' },
  { label: 'Medicaid', value: 'medicaid', category: 'government' },
  { label: 'Tricare', value: 'tricare', category: 'government' },
  { label: 'VA Health', value: 'va_health', category: 'government' },
  
  // Major Private Insurers
  { label: 'Aetna', value: 'aetna', category: 'private' },
  { label: 'Anthem Blue Cross Blue Shield', value: 'anthem_bcbs', category: 'private' },
  { label: 'Blue Cross Blue Shield', value: 'bcbs', category: 'private' },
  { label: 'Cigna', value: 'cigna', category: 'private' },
  { label: 'Humana', value: 'humana', category: 'private' },
  { label: 'Kaiser Permanente', value: 'kaiser', category: 'private' },
  { label: 'UnitedHealthcare', value: 'unitedhealthcare', category: 'private' },
  { label: 'AARP', value: 'aarp', category: 'private' },
  { label: 'Ambetter', value: 'ambetter', category: 'private' },
  { label: 'Molina Healthcare', value: 'molina', category: 'private' },
  { label: 'Oscar Health', value: 'oscar', category: 'private' },
  { label: 'Centene', value: 'centene', category: 'private' },
  
  // Regional/State Plans
  { label: 'HealthFirst', value: 'healthfirst', category: 'regional' },
  { label: 'EmblemHealth', value: 'emblemhealth', category: 'regional' },
  { label: 'Independence Blue Cross', value: 'independence_bc', category: 'regional' },
  { label: 'Highmark', value: 'highmark', category: 'regional' },
  { label: 'Premera Blue Cross', value: 'premera', category: 'regional' },
  
  // Other
  { label: 'Self-Pay', value: 'self_pay', category: 'other' },
  { label: 'Workers Compensation', value: 'workers_comp', category: 'other' },
  { label: 'Other Private Insurance', value: 'other_private', category: 'other' },
] as const;

/**
 * Common US Counties by State (sample - can be expanded)
 */
export const US_COUNTIES: Record<string, string[]> = {
  CA: ['Los Angeles', 'San Diego', 'Orange', 'Riverside', 'San Bernardino', 'Santa Clara', 'Alameda', 'Sacramento', 'Contra Costa', 'Fresno'],
  TX: ['Harris', 'Dallas', 'Tarrant', 'Bexar', 'Travis', 'Collin', 'Fort Bend', 'Montgomery', 'Denton', 'Hidalgo'],
  FL: ['Miami-Dade', 'Broward', 'Palm Beach', 'Hillsborough', 'Orange', 'Pinellas', 'Duval', 'Brevard', 'Polk', 'Volusia'],
  NY: ['Kings', 'Queens', 'New York', 'Suffolk', 'Bronx', 'Nassau', 'Westchester', 'Erie', 'Monroe', 'Richmond'],
  IL: ['Cook', 'DuPage', 'Lake', 'Will', 'Kane', 'McHenry', 'Winnebago', 'Madison', 'St. Clair', 'Champaign'],
  PA: ['Philadelphia', 'Allegheny', 'Montgomery', 'Bucks', 'Delaware', 'Chester', 'Lancaster', 'York', 'Dauphin', 'Lehigh'],
  OH: ['Cuyahoga', 'Franklin', 'Hamilton', 'Summit', 'Montgomery', 'Lucas', 'Stark', 'Butler', 'Lorain', 'Mahoning'],
  GA: ['Fulton', 'Gwinnett', 'Cobb', 'DeKalb', 'Clayton', 'Chatham', 'Richmond', 'Muscogee', 'Bibb', 'Forsyth'],
  NC: ['Mecklenburg', 'Wake', 'Guilford', 'Forsyth', 'Cumberland', 'Durham', 'Buncombe', 'Gaston', 'Union', 'Cabarrus'],
  MI: ['Wayne', 'Oakland', 'Macomb', 'Kent', 'Genesee', 'Washtenaw', 'Ingham', 'Kalamazoo', 'Saginaw', 'Ottawa'],
};

/**
 * Get counties for a state
 */
export const getCountiesForState = (stateCode: string): string[] => {
  return US_COUNTIES[stateCode] || [];
};

/**
 * Common Healthcare Specialties in USA
 */
export const US_HEALTHCARE_SPECIALTIES = [
  'Family Medicine',
  'Internal Medicine',
  'Pediatrics',
  'Cardiology',
  'Dermatology',
  'Orthopedics',
  'Neurology',
  'Psychiatry',
  'Obstetrics & Gynecology',
  'Ophthalmology',
  'ENT (Ear, Nose & Throat)',
  'Urology',
  'Gastroenterology',
  'Pulmonology',
  'Endocrinology',
  'Rheumatology',
  'Oncology',
  'Emergency Medicine',
  'Urgent Care',
  'Physical Therapy',
  'Occupational Therapy',
  'Mental Health Counseling',
  'Chiropractic',
  'Dental',
  'Optometry',
] as const;

/**
 * Common Languages Spoken in USA
 */
export const US_LANGUAGES = [
  'English',
  'Spanish',
  'Chinese (Mandarin)',
  'Chinese (Cantonese)',
  'Tagalog',
  'Vietnamese',
  'Arabic',
  'French',
  'Korean',
  'Russian',
  'German',
  'Hindi',
  'Portuguese',
  'Italian',
  'Polish',
  'Japanese',
  'Urdu',
  'Persian',
  'Gujarati',
  'Greek',
  'Hebrew',
  'Bengali',
  'Punjabi',
  'Telugu',
  'Tamil',
] as const;

